import { cloneDeep } from 'lodash-es';
import { BetDateType } from '@/constants';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { applyDatePeriodPresetToBetDate } from '@/helpers/applyDatePeriodPresetToBetDate';
import { createOptionalSportFilter, resetOptionalSportFilter } from '@/helpers/sportFilter';
import { WlBalanceItem } from '@/models/wlBalance';
import { createWlBalanceTableHeaders } from '@/pages/accounting/wlBalance/WlBalanceTableHeaders';
import i18n from '@/plugins/i18n';
import { isRequestCanceled } from '@/services/baseService';
import { wlBalanceService } from '@/services/wlBalanceService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { useUserStore } from '@/stores/user';
import { IPageOptions } from '@/types/page';
import type { DataTableHeader } from '@/types/vuetify';
import { IWlBalanceFilters } from '@/types/wlBalance';

function createWlBalanceStoreSetup(storeId: string) {
    // --- state ---
    const t = i18n.global.t;

    const items = ref<WlBalanceItem[]>([]);

    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const isExporting = ref(false);

    // --- filters ---
    const filters = reactive<IWlBalanceFilters>({
        sportId: createOptionalSportFilter(),
        betDateType: {
            value: BetDateType.PlacementGMT,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        datePeriod: {
            value: 'range',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bookBalance.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        userObserveId: {
            value: null,
            title: 'bets.userObserveId',
            allowSaveState: true,
        },
        betSelectionId: {
            value: null,
            title: 'bets.betType',
            allowSaveState: true,
        },
        userId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
        betBetVsOpen: {
            value: null,
            title: 'bets.betVsOpen',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const userStore = useUserStore();
    const filterPanel = useFilterPanelState(filters);
    const availableFilters = computed(() => filterPanel.availableFilters.value.filter((filter) => filter.key !== 'betBetVsOpen' || userStore.isAdmin()));
    const visibleFilters = computed(() => filterPanel.visibleFilters.value.filter((filterName) => filterName !== 'betBetVsOpen' || userStore.isAdmin()));
    const { hiddenFilters, visibleFiltersMobile, filterOrder, useButtonForFiltersApply, saveFiltersState } = filterPanel;

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 500,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        resetOptionalSportFilter(filters.sportId);
        filters.betDateType.value = BetDateType.PlacementGMT;
        filters.datePeriod.value = 'range';
        filters.betDate.value = todayDateRangeInUserTimeZone();
        filters.betTradingTypeId.value = null;
        filters.betEventPeriodId.value = null;
        filters.betEventType.value = null;
        filters.userObserveId.value = null;
        filters.betSelectionId.value = null;
        filters.userId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
        filters.betBetVsOpen.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createWlBalanceTableHeaders());

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'wlBalance',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadAllData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            let filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Site', 'CurrencyAll', 'Agent', 'Platform'],
            };

            const response = await wlBalanceService.list(params);
            items.value = (response.data.rows ?? []).map((row) => new WlBalanceItem(row));
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    const exportColumns = {
        platform_id: t('wlBalance.platform'),
        bet_count: t('wlBalance.betCount'),
        settled_stake_sum: t('wlBalance.playerStake'),
        balance: t('wlBalance.playerPl'),
        wl_to_tb: t('wlBalance.wlToTb'),
        final_operation: t('wlBalance.a2fFinalOperation'),
        balance_bookmaker_brl: t('wlBalance.tbToBookiesBrl'),
        balance_bookmaker_euro: t('wlBalance.tbToBookiesEur'),
        tb_final_operation_brl: t('wlBalance.tbFinalOperationBrl'),
        tb_final_operation_euro: t('wlBalance.tbFinalOperationEur'),
        tb_book_commissions_euro: t('wlBalance.tbBookCommissionsEur'),
    };

    async function exportData() {
        isExporting.value = true;
        try {
            let filtersCopy = cloneDeep(filters);
            applyDatePeriodPresetToBetDate(filtersCopy);

            const params: any = {
                filters: filtersCopy,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: 1,
                    itemsPerPage: 500,
                },
                requiredData: [],
                requiredDictionaries: [],
                columns: JSON.stringify(exportColumns),
            };

            const response = await wlBalanceService.export(params);

            let filename = 'wl-balance.xlsx';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export error:', e);
            throw e;
        } finally {
            isExporting.value = false;
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadAllData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadAllData();
        }
    }

    function clearData() {
        items.value = [];
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadAllData,
        updateOptions,
        clearData,
        isExporting,
        exportData,
        stats: undefined,
        hasExport: true,
        hasRefresh: true,
    };
}

export function useWlBalanceStore(storeId: string) {
    const useStore = defineStore(storeId, () => createWlBalanceStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'wlBalance'),
    });
    return useStore();
}

export type WlBalanceStoreSetup = ReturnType<typeof useWlBalanceStore>;
